import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import {
  LAWYER_TYPE_LAWYER,
  LAWYER_TYPE_ADVOCATE,
  LAWYER_TYPE_LAWYER_COMPANY,
  LAWYER_TYPE_ADVOCATE_COMPANY,
} from '@/shared/constants/lawyerTypeConstants';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { extractList } from '@/shared/utils/unwrapApiResponse';
import { useUserStore } from '@/entities/user/store';
import type {
  ILawyerCardInfo,
  ILawyerDetail,
} from '@/entities/lawyer/model/types/lawyer';

const TITLES: Record<number, string> = {
  [LAWYER_TYPE_LAWYER]: 'Юристы этого города',
  [LAWYER_TYPE_ADVOCATE]: 'Адвокаты этого города',
  [LAWYER_TYPE_LAWYER_COMPANY]: 'Юридические организации этого города',
  [LAWYER_TYPE_ADVOCATE_COMPANY]: 'Юридические организации этого города',
};

const FILTER_QUERY: Record<number, string> = {
  [LAWYER_TYPE_LAWYER]: '?is_lawyer=1',
  [LAWYER_TYPE_ADVOCATE]: '?is_advocate=1',
  [LAWYER_TYPE_LAWYER_COMPANY]: '?is_organizations=1',
  [LAWYER_TYPE_ADVOCATE_COMPANY]: '?is_organizations=1',
};

const resolveType = (info: ILawyerDetail | null): number => {
  if (info?.company || info?.company_id) {
    return info?.role === LAWYER_TYPE_ADVOCATE
      ? LAWYER_TYPE_ADVOCATE_COMPANY
      : LAWYER_TYPE_LAWYER_COMPANY;
  }
  return info?.role ?? LAWYER_TYPE_LAWYER;
};

const isCompanyType = (type: number): boolean =>
  type === LAWYER_TYPE_LAWYER_COMPANY || type === LAWYER_TYPE_ADVOCATE_COMPANY;

export const useCityLawyers = () => {
  const lawyerInfo = inject(LAWYER_INFO);
  const route = useRoute();
  const userStore = useUserStore();
  const { $lawyerService } = useNuxtApp();

  const lawyerType: ComputedRef<number> = computed(() =>
    resolveType(lawyerInfo?.value ?? null),
  );

  const title: ComputedRef<string> = computed(
    () => TITLES[lawyerType.value] ?? TITLES[LAWYER_TYPE_LAWYER] ?? '',
  );

  const address: ComputedRef<{ city?: { slug?: string } } | null> = computed(
    () => lawyerInfo?.value?.addresses?.[0] ?? null,
  );

  const citySlug: ComputedRef<string | null> = computed(() => {
    const region = route.params.region;
    const fromRoute = Array.isArray(region) ? region[0] : region;
    return fromRoute || address.value?.city?.slug || null;
  });

  const link: ComputedRef<string> = computed(() => {
    const slug = address.value?.city?.slug;
    if (!slug) return ROOT_CATALOG_URL;
    return `${ROOT_CATALOG_URL}geo/${slug}/${FILTER_QUERY[lawyerType.value] ?? ''}`;
  });

  const fetchLawyers = (): Promise<unknown> => {
    const type = lawyerType.value;
    const isCompany = isCompanyType(type);
    const dataReq = {
      city: citySlug.value,
      is_lawyer: type === LAWYER_TYPE_LAWYER ? '1' : null,
      is_advocate: type === LAWYER_TYPE_ADVOCATE ? '1' : null,
      is_lawyer_company: isCompany ? '1' : null,
      is_advocate_company: isCompany ? '1' : null,
    };
    return $lawyerService.getLawyers(dataReq, {
      per_page: 10,
      type,
      sort: 'rating',
    });
  };

  const { data: lawyers } = useAsyncData<ILawyerCardInfo[]>(
    () =>
      `city-lawyers:${lawyerInfo?.value?.id ?? 'none'}:${citySlug.value ?? ''}:${lawyerType.value}`,
    async () => {
      if (!lawyerInfo?.value?.id) return [];
      const list = extractList<ILawyerCardInfo>(await fetchLawyers());
      const ownLawyerId = userStore.user?.lawyer?.id;
      return list.filter(
        (item) => item.id !== lawyerInfo?.value?.id && item.id !== ownLawyerId,
      );
    },
    {
      default: () => [],
      watch: [() => lawyerInfo?.value?.id, citySlug],
      getCachedData: (key, nuxtApp, ctx) =>
        ctx.cause === 'initial' ? nuxtApp.payload.data[key] : undefined,
    },
  );

  const isMobile: boolean = useIsMobile();

  const dotsCount: ComputedRef<number> = computed(() => {
    const count = lawyers.value?.length ?? 0;
    const snaps = isMobile ? count : Math.ceil(count / 2);
    return snaps > 1 ? snaps : 0;
  });

  return { lawyers, title, link, dotsCount };
};

export default useCityLawyers;
